import { useEffect, useRef, useState } from "react";
import { MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import ReactionButtons from "./ReactionButtons";
import CommentsSection from "./CommentsSection";
import MentionText from "./MentionText";
import { useStateValue } from "../../context/StateProvider";
import { roleLabel } from "../../utils/feedHelpers";
import confetti from "canvas-confetti";
import ReactionsModal from "./ReactionsModal";

// Format Date.now() ms as a short relative time string.
function timeAgo(ts) {
  if (!ts) return "";
  const m = Math.floor((Date.now() - Number(ts)) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return "yesterday";
  if (d < 7) return `${d}d ago`;
  return new Date(Number(ts)).toLocaleDateString();
}

function initialsFor(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return ((parts[0] || "?")[0] || "?").toUpperCase().padEnd(2, "?");
}

// Props:
//   post: full post object
//   currentUser: { id, role, ... }
//   onReact: (reaction: "like" | "dislike") => void
//   onEdit: () => void
//   onDelete: () => void
function PostCard({ post, currentUser, onReact, onEdit, onDelete }) {
  const [{ users }] = useStateValue();
  // For the current user's own posts, prefer currentUser directly — avoids
  // depending on a successful match in the all-users array (which can miss
  // if a user doc was created without an explicit `id` field).
  const isMe = post.authorId && post.authorId === currentUser?.id;
  const author = isMe
    ? currentUser
    : Array.isArray(users)
      ? users.find((u) => u.id === post.authorId)
      : null;
  // Prefer the photo captured on the post at create time; fall back to a live
  // user lookup so old posts (and any posts where the author updated their pic)
  // still resolve to the current photo.
  const authorPhoto = post.authorPhoto || author?.profilepicture || "";
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [reactorsKey, setReactorsKey] = useState(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const isOwner = post.authorId === currentUser?.id;
  const isAdmin = currentUser?.role === "Admin";
  const canEdit = isOwner;
  const canDelete = isOwner || isAdmin;
  const currentReaction = post.reactions?.[currentUser?.id] || null;

  return (
    <div className="bg-white border border-black/[0.06] rounded-xl shadow-sm p-5 mb-4">
      <div className="flex items-start gap-3 mb-3">
        {authorPhoto ? (
          <img
            src={authorPhoto}
            alt={post.authorName || "User"}
            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-slate-700 text-white text-[12px] font-bold flex items-center justify-center flex-shrink-0">
            {initialsFor(post.authorName)}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[14px] font-semibold text-slate-900">
              {post.authorName || "Unknown"}
            </span>
            {post.authorRole && (
              <span className="text-[10px] uppercase tracking-wider font-semibold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                {roleLabel(post.authorRole)}
              </span>
            )}
          </div>
          <div className="text-[11.5px] text-gray-500 mt-0.5">
            {timeAgo(post.createdAt)}
            {post.updatedAt && (
              <span className="ml-2 text-gray-400">(edited)</span>
            )}
          </div>
        </div>
        {(canEdit || canDelete) && (
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
            >
              <MoreHorizontal size={16} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-7 z-10 w-36 bg-white border border-gray-200 rounded-md shadow-lg py-1">
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onEdit?.();
                    }}
                    className="w-full text-left px-3 py-1.5 text-[12.5px] text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Pencil size={13} /> Edit
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onDelete?.();
                    }}
                    className="w-full text-left px-3 py-1.5 text-[12.5px] text-rose-700 hover:bg-rose-50 flex items-center gap-2"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {post.text && (
        <div className="text-[14px] text-slate-800 whitespace-pre-wrap mb-3 leading-relaxed">
          <MentionText text={post.text} />
        </div>
      )}

      {Array.isArray(post.imageUrls) && post.imageUrls.length > 0 && (
        <div className="mb-3">
          <img
            src={post.imageUrls[0]}
            alt="post attachment"
            className="max-h-96 rounded-md border border-gray-200"
          />
        </div>
      )}

      <ReactionButtons
        currentReaction={currentReaction}
        counts={post.reactionCounts}
        onReact={(key) => {
          // Confetti only on a NEW party reaction (not toggle-off / switch-away).
          if (key === "party" && currentReaction !== "party") {
            confetti({
              particleCount: 80,
              spread: 70,
              origin: { y: 0.7 },
            });
          }
          onReact(key);
        }}
        onShowReactors={(key) => setReactorsKey(key)}
      />

      {reactorsKey && (
        <ReactionsModal
          reactions={post.reactions}
          initialKey={reactorsKey}
          onClose={() => setReactorsKey(null)}
        />
      )}

      <CommentsSection post={post} />
    </div>
  );
}

export default PostCard;
